import { useServiceProviderScope } from '@product-framework/pages/ServiceProvider/scope';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { useCallback, useMemo, useRef } from 'react';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  ButtonContainer,
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import schema from './edit.json';
import validationSchema from './validations';

const FORM_ID = 'ServiceProviderInsightSettingForm';
const PERMISSION_SP = 'SERVICEPROVIDERPROFILE';

function InsightSetting({
  serviceProviderId: serviceProviderIdProp,
  onChange = () => undefined,
}) {
  const scope = useServiceProviderScope();
  const serviceProviderId = scope.serviceProviderId ?? serviceProviderIdProp;
  const formRef = useRef();
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get, put } = useDataProvider(provider);

  const apiUrl = useMemo(() => {
    if (scope.endpoints?.serviceProviderProfileInsight) {
      return scope.endpoints.serviceProviderProfileInsight;
    }
    return `${getApiUrl()}/sxcl/v1/service-provider-profile/${serviceProviderId}/insight`;
  }, [
    scope.endpoints?.serviceProviderProfileInsight,
    getApiUrl,
    serviceProviderId,
  ]);

  const preProcessFetchData = data => {
    const { insightStatus } = data;
    return {
      insightStatus: insightStatus === 'ACTIVE',
    };
  };

  const preProcessRequestData = data => {
    const { insightStatus, ...other } = data;
    return {
      insightStatus: ['false', false].includes(insightStatus)
        ? 'INACTIVE'
        : 'ACTIVE',
      ...other,
    };
  };

  // Fetch Insight setting data
  const fetchDefaultValues = useCallback(async () => {
    const response = await get(apiUrl);
    if (response?.ok) {
      const updatePageData = preProcessFetchData(response?.data);
      const result = {
        data: updatePageData,
        errors: null,
      };
      return result;
    }
    const result = {
      data: null,
      errors: response?.errors,
    };
    return result;
  }, [apiUrl, get]);

  const onSubmit = async data => {
    const body = preProcessRequestData(data);
    const response = await put(apiUrl, body);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'INSIGHT_SETTING_UPDATE_SUCCESS',
          defaultMessage: 'Insight System settings were saved successfully',
        }),
      );
      const updatedPageData = preProcessFetchData(response?.data);
      return {
        nextValues: updatedPageData,
        errors: null,
        callback: () => {
          onChange();
        },
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  return (
    <PageSection>
      <Form
        formId={FORM_ID}
        ref={formRef}
        permissionEntities={[PERMISSION_SP]}
        onSubmit={onSubmit}
        schema={schema}
        validationSchema={validationSchema(formatMessage)}
        fetchDefaultValues={fetchDefaultValues}>
        <ButtonContainer>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_INSIGHT"
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'INSIGHT_SETTING_SAVE_BUTTON',
              defaultMessage: 'Save Insight Settings',
            })}
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_INSIGHT"
            variant="contained"
            startIcon={<IconLibrary.Reset />}
            color="primary"
            onClick={() => formRef.current?.reset()}>
            {formatMessage({
              id: 'INSIGHT_SETTING_RESET_BUTTON',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
        </ButtonContainer>
      </Form>
    </PageSection>
  );
}

export default InsightSetting;
